import React, { useCallback, useEffect, useState } from "react";
import {
  append,
  find,
  lensPath,
  lensProp,
  map,
  mergeRight,
  pick,
  pickBy,
  propEq,
  reject,
} from "ramda";
import { caseInsensitiveSort } from "../../util/sorts";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import { Box, List, ListItem, ListItemText } from "@material-ui/core";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import { useOrganizations } from "../../user-admin/userServices";
import OrganizationAutoComplete from "../../user-admin/OrganizationAutoComplete";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import {
  validateEmailAddressField,
  validateProjectEmail,
  validateProjectEmailField,
} from "../cspProjectValidation";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const ProjectEmailDialog = ({ project, open, onCancel, onApply }) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    emailTemplate: null,
    emailFromAddress: null,
    emailSubject: null,
    managerEmailSubject: null,
    projectManagerEmails: [],
  });

  useEffect(() => {
    if (notBlank(project)) {
      setFields(prev =>
        mergeRight(
          prev,
          pickBy(
            (val, _key) => notBlank(val),
            pick(
              [
                "emailTemplate",
                "emailFromAddress",
                "emailSubject",
                "managerEmailSubject",
                "projectManagerEmails",
              ],
              project,
            ),
          ),
        ),
      );
    }
  }, [project]);

  const [organizations, setOrganizations] = useState([]);
  const organizationsResponse = useOrganizations();
  useEffect(() => {
    if (
      organizationsResponse.isSuccess &&
      notBlank(organizationsResponse.data)
    ) {
      setOrganizations(organizationsResponse.data);
    }
  }, [organizationsResponse.isSuccess, organizationsResponse.data]);

  const findOrganizationName = useCallback(
    orgId => {
      const organization = find(propEq("organizationId", orgId), organizations);
      return notBlank(organization) ? organization.organizationName : null;
    },
    [organizations],
  );

  const [emailAddress, setEmailAddress] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleAddEmailClick = useCallback(() => {
    const errors = validateEmailAddressField("emailAddress", emailAddress);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      setFields(prev =>
        set(
          lensProp("projectManagerEmails"),
          append({ emailAddress, organizationId }, prev.projectManagerEmails),
          prev,
        ),
      );
      setEmailAddress("");
      setOrganizationId(null);
      removeValidationError("emailAddress");
    }
  }, [emailAddress, organizationId, addValidationError, removeValidationError]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleBlur = (field, value) => {
    const errors = validateProjectEmailField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleDeleteEmail = id => {
    setFields(prev =>
      set(
        lensProp("projectManagerEmails"),
        reject(propEq("projectManagerEmailId", id), prev.projectManagerEmails),
        prev,
      ),
    );
  };

  const handleApply = async () => {
    const validationErrors = validateProjectEmail(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      onApply(fields);
    }
  };

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="form-dialog-title">Edit Email Properties</DialogTitle>
      <DialogContent>
        <DialogErrors />
        <DialogContentText>
          Enter properties for Email in the Project.
        </DialogContentText>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12}>
            <TextField
              id="email-template"
              fullWidth
              label="Email Template"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.emailTemplate}
              onChange={handleTextChange(["emailTemplate"])}
              error={hasValidationError("emailTemplate")}
              helperText={firstValidationError("emailTemplate")}
              onBlur={() => handleBlur("emailTemplate", fields.emailTemplate)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="email-from-address"
              fullWidth
              label="Email From Address"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.emailFromAddress}
              onChange={handleTextChange(["emailFromAddress"])}
              error={hasValidationError("emailFromAddress")}
              helperText={firstValidationError("emailFromAddress")}
              onBlur={() =>
                handleBlur("emailFromAddress", fields.emailFromAddress)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="email-subject"
              fullWidth
              label="Email Subject"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.emailSubject}
              onChange={handleTextChange(["emailSubject"])}
              error={hasValidationError("emailSubject")}
              helperText={firstValidationError("emailSubject")}
              onBlur={() => handleBlur("emailSubject", fields.emailSubject)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="manager-email-subject"
              fullWidth
              label="Manager Email Subject"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.managerEmailSubject}
              onChange={handleTextChange(["managerEmailSubject"])}
              error={hasValidationError("managerEmailSubject")}
              helperText={firstValidationError("managerEmailSubject")}
              onBlur={() =>
                handleBlur("managerEmailSubject", fields.managerEmailSubject)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5">Manager Emails</Typography>
            {notBlank(fields.projectManagerEmails) ? (
              <List dense>
                {map(
                  managerEmail => (
                    <ListItem
                      key={
                        managerEmail.projectManagerEmailId ||
                        managerEmail.emailAddress
                      }
                      divider
                    >
                      <ListItemText
                        primary={managerEmail.emailAddress}
                        secondary={findOrganizationName(
                          managerEmail.organizationId,
                        )}
                      />
                      <ListItemSecondaryAction>
                        <ConfirmButton
                          handleDelete={() =>
                            handleDeleteEmail(
                              managerEmail.projectManagerEmailId,
                            )
                          }
                          dialogTitle="Remove Manager Email"
                          dialogBody="Are you sure you want to remove manager email?"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ),
                  caseInsensitiveSort("emailAddress")(
                    fields.projectManagerEmails,
                  ),
                )}
              </List>
            ) : (
              <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                No Manager Emails are set up for this Project.
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="email-address"
              fullWidth
              label="New Manager Email Address"
              InputProps={{
                style: { fontSize: "1.8rem" },
              }}
              value={emailAddress || ""}
              onChange={event => setEmailAddress(event.target.value)}
              error={hasValidationError("emailAddress")}
              helperText={firstValidationError("emailAddress")}
            />
          </Grid>
          <Grid item xs={12}>
            <OrganizationAutoComplete
              value={organizationId}
              filterFunction={org => org.active}
              onChange={setOrganizationId}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={handleAddEmailClick}
              disabled={isBlank(emailAddress)}
              aria-label="add option"
            >
              Add Email
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectEmailDialog;
